import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Box,
  Button,
  Chip
} from '@mui/material';
import { 
  Key as KeyIcon, 
  Add as AddIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';

const CloudInitConfigForm = ({
  config,
  onConfigChange,
  onSshKeysChange,
  onPackagesChange,
  onRuncmdChange,
  hostSSHKey,
  onLoadHostSSHKey,
  showAdvanced = true,
  helperTextSuffix = ""
}) => {
  // SSH 키 추가/제거 핸들러
  const handleAddSSHKey = () => {
    const newKey = '';
    const currentKeys = Array.isArray(config.ssh_authorized_keys) ? config.ssh_authorized_keys : [];
    onSshKeysChange({ target: { value: [...currentKeys, newKey].join('\n') } });
  };

  const handleRemoveSSHKey = (index) => {
    const currentKeys = Array.isArray(config.ssh_authorized_keys) ? config.ssh_authorized_keys : [];
    const newKeys = currentKeys.filter((_, i) => i !== index);
    onSshKeysChange({ target: { value: newKeys.join('\n') } });
  };

  const handleSSHKeyChange = (index, value) => {
    const currentKeys = Array.isArray(config.ssh_authorized_keys) ? config.ssh_authorized_keys : [];
    const newKeys = [...currentKeys];
    newKeys[index] = value;
    onSshKeysChange({ target: { value: newKeys.join('\n') } });
  };

  // 패키지 추가/제거 핸들러
  const handleAddPackage = () => {
    const currentPackages = Array.isArray(config.packages) ? config.packages : [];
    onPackagesChange({ target: { value: [...currentPackages, ''].join('\n') } });
  };

  const handleRemovePackage = (index) => {
    const currentPackages = Array.isArray(config.packages) ? config.packages : [];
    const newPackages = currentPackages.filter((_, i) => i !== index);
    onPackagesChange({ target: { value: newPackages.join('\n') } });
  };

  const handlePackageChange = (index, value) => {
    const currentPackages = Array.isArray(config.packages) ? config.packages : [];
    const newPackages = [...currentPackages];
    newPackages[index] = value;
    onPackagesChange({ target: { value: newPackages.join('\n') } });
  };

  // 실행 명령 추가/제거 핸들러
  const handleAddCommand = () => {
    const currentCommands = Array.isArray(config.runcmd) ? config.runcmd : [];
    onRuncmdChange({ target: { value: [...currentCommands, ''].join('\n') } });
  };

  const handleRemoveCommand = (index) => {
    const currentCommands = Array.isArray(config.runcmd) ? config.runcmd : [];
    const newCommands = currentCommands.filter((_, i) => i !== index);
    onRuncmdChange({ target: { value: newCommands.join('\n') } });
  };

  const handleCommandChange = (index, value) => {
    const currentCommands = Array.isArray(config.runcmd) ? config.runcmd : [];
    const newCommands = [...currentCommands];
    newCommands[index] = value;
    onRuncmdChange({ target: { value: newCommands.join('\n') } });
  };
  return (
    <>
      {/* 기본 설정 */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          기본 설정
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="호스트명"
          name="hostname"
          value={config.hostname}
          onChange={onConfigChange}
          required
          helperText={`VM의 호스트명을 설정합니다${helperTextSuffix}`}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="사용자명"
          name="username"
          value={config.username}
          onChange={onConfigChange}
          required
          helperText={`초기 사용자 계정명${helperTextSuffix}`}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="비밀번호"
          name="password"
          type="password"
          value={config.password}
          onChange={onConfigChange}
          required
          helperText={`사용자 계정 비밀번호${helperTextSuffix}`}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>시간대</InputLabel>
          <Select
            name="timezone"
            value={config.timezone}
            onChange={onConfigChange}
            label="시간대"
          >
            <MenuItem value="Asia/Seoul">Asia/Seoul</MenuItem>
            <MenuItem value="UTC">UTC</MenuItem>
            <MenuItem value="America/New_York">America/New_York</MenuItem>
            <MenuItem value="Europe/London">Europe/London</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>로케일</InputLabel>
          <Select
            name="locale"
            value={config.locale}
            onChange={onConfigChange}
            label="로케일"
          >
            <MenuItem value="ko_KR.UTF-8">ko_KR.UTF-8 (한국어)</MenuItem>
            <MenuItem value="en_US.UTF-8">en_US.UTF-8 (English)</MenuItem>
            <MenuItem value="ja_JP.UTF-8">ja_JP.UTF-8 (日本語)</MenuItem>
            <MenuItem value="zh_CN.UTF-8">zh_CN.UTF-8 (中文)</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* SSH 설정 */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          SSH 설정
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              name="enable_ssh"
              checked={config.enable_ssh}
              onChange={onConfigChange}
            />
          }
          label="SSH 서비스 활성화"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="SSH 포트"
          name="ssh_port"
          type="number"
          value={config.ssh_port}
          onChange={onConfigChange}
          helperText="SSH 서비스 포트 (기본: 22)"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              name="ssh_password_auth"
              checked={config.ssh_password_auth}
              onChange={onConfigChange}
            />
          }
          label="비밀번호 인증"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              name="ssh_key_auth"
              checked={config.ssh_key_auth}
              onChange={onConfigChange}
            />
          }
          label="SSH 키 인증"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              name="ssh_root_login"
              checked={config.ssh_root_login}
              onChange={onConfigChange}
            />
          }
          label="Root 로그인 허용"
        />
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 2 }}>
              SSH 공개키 (선택사항)
            </Typography>
            <Tooltip title={hostSSHKey ? "호스트 SSH 키 불러오기" : "호스트 SSH 키가 없습니다"}>
              <span>
                <IconButton
                  onClick={onLoadHostSSHKey}
                  disabled={!hostSSHKey}
                  size="small"
                  color="primary"
                >
                  <KeyIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSSHKey}
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            >
              SSH 키 추가
            </Button>
          </Box>
          
          {Array.isArray(config.ssh_authorized_keys) && config.ssh_authorized_keys.length > 0 ? (
            config.ssh_authorized_keys.map((key, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  fullWidth
                  label={`SSH 키 ${index + 1}`}
                  multiline
                  rows={2}
                  value={key}
                  onChange={(e) => handleSSHKeyChange(index, e.target.value)}
                  placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAA... 또는 ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA..."
                  sx={{ mr: 1 }}
                />
                <IconButton
                  onClick={() => handleRemoveSSHKey(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                SSH 키가 없습니다. "SSH 키 추가" 버튼을 클릭하여 추가하세요.
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            💡 각 SSH 공개키는 별도의 입력란에 작성하세요. 호스트 키는 🔑 버튼으로 불러올 수 있습니다.
          </Typography>
        </Box>
      </Grid>

      {/* 추가 설정 */}
      {showAdvanced && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              추가 설정 (선택사항)
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  설치할 패키지 (선택사항)
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddPackage}
                  size="small"
                  variant="outlined"
                >
                  패키지 추가
                </Button>
              </Box>
              
              {Array.isArray(config.packages) && config.packages.length > 0 ? (
                config.packages.map((pkg, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`패키지 ${index + 1}`}
                      value={pkg}
                      onChange={(e) => handlePackageChange(index, e.target.value)}
                      placeholder="예: curl, vim, htop, docker.io"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleRemovePackage(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    설치할 패키지가 없습니다. "패키지 추가" 버튼을 클릭하여 추가하세요.
                  </Typography>
                </Box>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                💡 각 패키지는 별도의 입력란에 작성하세요 (apt/yum 패키지명).
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  실행할 명령 (선택사항)
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddCommand}
                  size="small"
                  variant="outlined"
                >
                  명령 추가
                </Button>
              </Box>
              
              {Array.isArray(config.runcmd) && config.runcmd.length > 0 ? (
                config.runcmd.map((cmd, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`명령 ${index + 1}`}
                      multiline
                      rows={1}
                      value={cmd}
                      onChange={(e) => handleCommandChange(index, e.target.value)}
                      placeholder="예: systemctl enable docker, ufw enable, apt update"
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => handleRemoveCommand(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    실행할 명령이 없습니다. "명령 추가" 버튼을 클릭하여 추가하세요.
                  </Typography>
                </Box>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                💡 각 명령은 별도의 입력란에 작성하세요. 초기화 시점에 root 권한으로 실행됩니다.
              </Typography>
            </Box>
          </Grid>
        </>
      )}
    </>
  );
};

export default CloudInitConfigForm;